import { useRef, useState } from "react";
import { IconRefresh } from "@tabler/icons-react";

import { api } from "../../lib/api.js";
import { Button } from "../ui/index.js";

/**
 * On-demand scoped OpenProject refresh for the live Payroll / Work Profiles
 * screens. Pulls only what that screen needs (scope: "payroll" or
 * "work_profiles"). Every admitted PayDay user has full access.
 */
export function ScopedRefreshControl({
  scope,
  label,
  onDone,
  compact = false,
  buttonText = "Refresh",
  disabled = false,
  busyText = "Refreshing…",
}: {
  scope: "payroll" | "work_profiles";
  label: string;
  onDone?: () => void | Promise<void>;
  compact?: boolean;
  buttonText?: string;
  disabled?: boolean;
  busyText?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshed, setRefreshed] = useState(false);
  const currentOnDone = useRef(onDone);
  currentOnDone.current = onDone;

  const submit = async () => {
    if (busy || disabled) return;
    setError(null);
    setRefreshed(false);
    setBusy(true);
    try {
      await api.scopedRefresh(scope, label);
      await currentOnDone.current?.();
      setRefreshed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scoped-refresh-control flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="min-h-11"
        iconOnly={compact}
        loading={busy}
        disabled={disabled}
        aria-label={label}
        onClick={submit}
      >
        {!busy && <IconRefresh size={16} aria-hidden="true" />}
        <span className={compact ? "sr-only" : undefined}>{busy ? busyText : buttonText}</span>
      </Button>
      <span className={busy || refreshed ? "text-xs text-foreground-muted" : "sr-only"} role="status">
        {busy ? "Refreshing source data…" : refreshed ? (scope === "work_profiles" ? "Work profiles refreshed" : "Payroll data refreshed") : ""}
      </span>
      {error && (
        <p className="max-w-xs text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

